import { motion } from "framer-motion";

/**
 * 5-dot Outfit progress bar — shown in the chat header for Level 28 (Outfit of the Holy Soldier)
 * and Level 29 (Armor of Primordeia) tiers. Each dot represents one of the five garments in
 * the current outfit; the active dot is the next piece to be earned.
 *
 * Returns null when the user is not on a Level 28/29 tier.
 */
const OUTFITS = {
  holy_soldier: {
    label: "Outfit of the Holy Soldier",
    short: "Holy Soldier",
    pieces: [27, 28, 29, 30, 31],
    emojis: ["\u{1F9E5}", "\u{1F456}", "\u{1F9E6}", "\u{1F462}", "\u{1FA96}"],
  },
  primordeia_armor: {
    label: "Armor of Primordeia",
    short: "Primordeia Armor",
    pieces: [32, 33, 34, 35, 36],
    emojis: ["\u{1F458}", "\u{1F6E1}", "\u{1F5E1}", "\u{1F6E1}", "\u26D1"],
  },
};

export function getActiveOutfit(currentTier) {
  if (currentTier >= 27 && currentTier <= 31) return OUTFITS.holy_soldier;
  if (currentTier >= 32 && currentTier <= 33) return OUTFITS.primordeia_armor;
  return null;
}

const OutfitProgressBar = ({ currentTier, completedGarments = [] }) => {
  const outfit = getActiveOutfit(currentTier);
  if (!outfit) return null;

  const earned = new Set(completedGarments);
  const earnedCount = outfit.pieces.filter((p) => earned.has(p)).length;

  return (
    <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: "rgba(13,10,26,0.92)" }}
      data-testid="outfit-progress-bar">
      <div className="flex-1">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[9px] font-bold uppercase tracking-[0.18em]"
            style={{ color: "#D4A574", fontFamily: "'Cinzel', serif" }}>
            {outfit.short}
          </span>
          <span className="text-[9px]" style={{ color: "#B8A8D8" }}>
            {earnedCount}/5
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {outfit.pieces.map((p, i) => {
            const has = earned.has(p);
            const isNext = !has && outfit.pieces.slice(0, i).every((q) => earned.has(q));
            return (
              <motion.div
                key={p}
                initial={false}
                animate={isNext ? { scale: [1, 1.18, 1] } : {}}
                transition={isNext ? { repeat: Infinity, duration: 1.6, ease: "easeInOut" } : {}}
                className="flex items-center justify-center rounded-full text-[10px]"
                style={{
                  width: 18,
                  height: 18,
                  background: has
                    ? "linear-gradient(135deg, #D4A574, #F2CC8F)"
                    : isNext
                    ? "linear-gradient(135deg, #4F378B, #7B5EA7)"
                    : "rgba(139,125,168,0.25)",
                  boxShadow: has
                    ? "0 0 8px rgba(212,165,116,0.7)"
                    : isNext
                    ? "0 0 8px rgba(123,94,167,0.6)"
                    : "none",
                  border: has ? "1px solid rgba(255,248,220,0.6)" : "1px solid rgba(139,125,168,0.3)",
                  color: has ? "#3D2A0E" : "#FFF",
                  opacity: has ? 1 : isNext ? 0.95 : 0.4,
                }}
                title={has ? "Earned" : isNext ? "Next piece" : "Locked"}
                data-testid={`outfit-dot-${i}`}
              >
                {has ? outfit.emojis[i] : ""}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OutfitProgressBar;
